import { useCurrentStudyStore } from '@src/store/dicom-web/current-study-store';
import z from 'zod';

export const MaskDataSchema = z.object({
  values: z.array(z.number()),
  coords: z.array(z.tuple([z.number(), z.number()])),
});
export type MaskData = z.infer<typeof MaskDataSchema>;

export type NormalizationMode = 'minmax' | 'percentile';

export class BiomarkerMask {
  private isDynamic: boolean;
  private biomarkerName: string;
  private imageUID: string;
  private data: MaskData;
  private sliceIndex: string;
  private refImageType: string | null;
  private range?: [number, number];
  private normalizationFactor?: number;
  private rawValues?: number[];

  // Caches: keyed by normalization mode
  private normalizedImageCache: Map<NormalizationMode, number[][]> = new Map();
  // Cache the raw 2D image so we don't re-fetch / re-shape on mode switch
  private rawImageCache: number[][] | null = null;
  private rawImageDimensions: { width: number; height: number } | null = null;

  constructor(
    isDynamic: boolean,
    biomarkerName: string,
    imageUID: string,
    data: MaskData,
    sliceIndex: string,
    refImageType: string | null,
    range?: [number, number],
    normalizationFactor?: number,
    rawValues?: number[],
  ) {
    this.isDynamic = isDynamic;
    this.biomarkerName = biomarkerName;
    this.sliceIndex = sliceIndex;
    this.imageUID = imageUID;
    this.data = data;
    this.refImageType = refImageType;
    this.range = range;
    this.normalizationFactor = normalizationFactor;
    this.rawValues = rawValues;
  }

  public getSliceIndex(): string {
    return this.sliceIndex;
  }

  public getIsDynamic(): boolean {
    return this.isDynamic;
  }

  public getBiomarkerName(): string {
    return this.biomarkerName;
  }

  public getRefImageType(): string | null {
    return this.refImageType;
  }

  public getImageUID(): string {
    return this.imageUID;
  }

  public getNormalizationFactor(): number | undefined {
    return this.normalizationFactor;
  }

  public setNormalizationFactor(factor: number): void {
    this.normalizationFactor = factor;
  }

  public getRawValues(): number[] | undefined {
    return this.rawValues;
  }

  public setRawValues(values: number[]): void {
    this.rawValues = values;
  }

  public getData(isNormalized = true): MaskData {
    if (!isNormalized) {
      if (this.rawValues && this.rawValues.length === this.data.values.length) {
        return {
          coords: this.data.coords,
          values: this.rawValues,
        };
      }
      if (typeof this.normalizationFactor === 'number' && Number.isFinite(this.normalizationFactor)) {
        return {
          coords: this.data.coords,
          values: this.data.values.map(v => v * this.normalizationFactor!),
        };
      }
    }
    return this.data;
  }

  /**
   * Returns the reference image normalized to [0, 255].
   *
   * @param mode
   *   - 'percentile' (default): clips at 1st–99th percentile before stretching.
   *     Best for medical images — keeps dark scans visible by ignoring outliers.
   *   - 'minmax': raw min/max linear stretch. Fast but vulnerable to outliers.
   */
  public async getRefImage(
    mode: NormalizationMode = 'percentile',
  ): Promise<number[][] | null> {
    // Cache hit for this mode
    const cached = this.normalizedImageCache.get(mode);
    if (cached) return cached;

    // Make sure we have the raw 2D image
    const raw = await this.loadRawImage();
    if (!raw) return null;

    // Normalize
    const normalized
      = mode === 'percentile'
        ? BiomarkerMask.normalizePercentile(raw)
        : BiomarkerMask.normalizeMinMax(raw);

    this.normalizedImageCache.set(mode, normalized);
    return normalized;
  }

  /**
   * Fetches the raw VTK image data once and caches it as a 2D number array.
   */
  private async loadRawImage(): Promise<number[][] | null> {
    if (this.rawImageCache) return this.rawImageCache;

    const currentStudyStore = useCurrentStudyStore();
    const imageData = await currentStudyStore.fetchOrGetVtkImageData(
      this.imageUID,
    );
    if (!imageData) return null;

    const targetImage = imageData.getPointData().getScalars().getData();
    const dimensions = imageData.getDimensions();
    const width = dimensions[0];
    const height = dimensions[1];

    const image2D: number[][] = Array.from({ length: height });
    for (let i = 0; i < height; i++) {
      const row: number[] = Array.from({ length: width });
      for (let j = 0; j < width; j++) {
        row[j] = targetImage[i * width + j];
      }
      image2D[i] = row;
    }

    this.rawImageCache = image2D;
    this.rawImageDimensions = { width, height };
    return image2D;
  }

  // ------------------------------------------------------------------
  // Normalization helpers
  // ------------------------------------------------------------------

  /**
   * Linear min/max normalization to [0, 255].
   * Simple, but a single bright/dark outlier can crush the contrast.
   */
  private static normalizeMinMax(image: number[][]): number[][] {
    let min = Infinity;
    let max = -Infinity;

    for (let i = 0; i < image.length; i++) {
      const row = image[i];
      for (let j = 0; j < row.length; j++) {
        const v = row[j];
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }

    const range = max - min;
    if (range === 0) {
      // Flat image — return all zeros to avoid divide-by-zero
      return image.map(row => row.map(() => 0));
    }

    const scale = 255 / range;
    return image.map(row =>
      row.map(v => Math.max(0, Math.min(255, Math.round((v - min) * scale)))),
    );
  }

  /**
   * Percentile-based ("windowed") normalization to [0, 255].
   *
   * This mimics standard medical-image windowing:
   *   1. Compute the 1st and 99th percentile of pixel intensities
   *   2. Clip everything below p1 → p1, above p99 → p99
   *   3. Linearly stretch [p1, p99] → [0, 255]
   *
   * This makes dark scans (e.g., low-contrast MRI/CT) actually visible
   * because outlier pixels (dead pixels, hot spots, background) don't
   * dominate the dynamic range.
   */
  private static normalizePercentile(
    image: number[][],
    lowPct = 0.01,
    highPct = 0.99,
  ): number[][] {
    const height = image.length;
    if (height === 0) return [];
    const width = image[0].length;
    const total = width * height;

    // Flatten into a typed array for fast sorting
    const flat = new Float64Array(total);
    let k = 0;
    for (let i = 0; i < height; i++) {
      const row = image[i];
      for (let j = 0; j < width; j++) {
        flat[k++] = row[j];
      }
    }

    // Sort ascending to compute percentiles
    // (Float64Array sort is in-place and uses numeric comparison by default)
    const sorted = flat.slice().sort();

    const lowIdx = Math.floor(lowPct * (total - 1));
    const highIdx = Math.floor(highPct * (total - 1));
    const lo = sorted[lowIdx];
    const hi = sorted[highIdx];

    const range = hi - lo;
    if (range <= 0) {
      // Degenerate case — fall back to plain min/max
      return BiomarkerMask.normalizeMinMax(image);
    }

    const scale = 255 / range;
    const out: number[][] = Array.from({ length: height });
    for (let i = 0; i < height; i++) {
      const row = image[i];
      const newRow: number[] = Array.from({ length: width });
      for (let j = 0; j < width; j++) {
        // Clip then stretch
        const clipped = Math.min(Math.max(row[j], lo), hi);
        newRow[j] = Math.round((clipped - lo) * scale);
      }
      out[i] = newRow;
    }
    return out;
  }

  // ------------------------------------------------------------------

  public getRange(isNormalized = true): [number, number] | undefined {
    if (!isNormalized) {
      if (this.range && typeof this.normalizationFactor === 'number' && Number.isFinite(this.normalizationFactor)) {
        return [this.range[0] * this.normalizationFactor, this.range[1] * this.normalizationFactor];
      }
      if (this.rawValues && this.rawValues.length > 0) {
        return [Math.min(...this.rawValues), Math.max(...this.rawValues)];
      }
      const rawData = this.getData(false);
      if (rawData.values.length > 0) {
        return [Math.min(...rawData.values), Math.max(...rawData.values)];
      }
    }
    if (this.range) {
      return this.range;
    }
    const { values } = this.data;
    if (values.length === 0) {
      return undefined;
    }
    const min = Math.min(...values);
    const max = Math.max(...values);
    this.range = [min, max];
    return this.range;
  }

  public getBoundingBox(): [number, number, number, number] {
    const { coords } = this.data;

    if (!coords || coords.length === 0) {
      console.warn('No coordinates found in mask data');
      return [0, 0, 0, 0];
    }

    const y = coords.map(c => c[0]);
    const x = coords.map(c => c[1]);

    const minY = Math.min(...y);
    const maxY = Math.max(...y);
    const minX = Math.min(...x);
    const maxX = Math.max(...x);

    return [
      Math.floor(minX),
      Math.floor(minY),
      Math.ceil(maxX),
      Math.ceil(maxY),
    ];
  }
}
